import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { state } from '../../store/global';
import API from '../../lib/api';
import {
  Roles,
  IDType,
  WalletType,
  EuropeRegion,
  getAccountType,
  ColombiaBanks,
  UsBanks,
  MexicoBanks,
  EuropeBanks,
  BrazilBanks,
  PixKeyTypes,
  emailValidator,
} from '../../utils/constants';
import findMessage from '../../utils/messages';

import Notification from '../../components/common/Notification';
import TransferHeader from './TransferHeader';
import AccountItem from './AccountItem';
import Searchbar from './Searchbar';
import IconButton from './IconButton';
import NormalButton from './NormalButton';
import Inputbox from './Inputbox';
import NormalSelectbox from './NormalSelectbox';
import BankSelectbox from './BankSelectbox';
import CurrencyConversionForm from './CurrencyConversionForm';
import TransferConfirmForm from './TransferConfirmForm';
import TransferFinalForm from './TransferFinalForm';
import SmsVerifyModal from './SmsVerifyModal';

import PlusIcon from '../../assets/images/plus.svg';
import ArrowRightIcon from '../svg/ArrowRightIcon';

export default function TransferForm({ country, paymentRoute, goBack, initialIsNewAccount = false }) {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const [progress, setProgress] = useState(0);
  // Idempotencia del retiro COP: un id por intento; se renueva tras exito o rechazo 4xx.
  const retiroRequestIdRef = useRef(null);
  const [searchKeyword, setSearchKeyword] = useState('');

  // progress == 0
  const [isNewAccount, setIsNewAccount] = useState(Boolean(initialIsNewAccount));
  const onToPrev = () => {
    setErrMessage('');
    if (isNewAccount) setIsNewAccount(false);
    else goBack();
  };
  const [notification, setNotification] = useState(null);
  const [recipientType, setRecipientType] = useState(''); // individual, business
  const [firstName, setFirstName] = useState(''); //
  const [lastName, setLastName] = useState(''); //
  const [email, setEmail] = useState(''); //
  const [businessName, setBusinessName] = useState('');
  const [docType, setDocType] = useState(''); // Cédula de ciudadanía, NIT
  const [docNumber, setDocNumber] = useState('');
  const [countryIsoCode3, setCountryIsoCode3] = useState('');
  const [street, setStreet] = useState('');
  const [state1, setState1] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [bank, setBank] = useState({ name: '', image: '' });
  const [bankAccountOwnerName, setBankAccountOwnerName] = useState('');
  const [accountType, setAccountType] = useState(''); // Cuenta de ahorros, Cuenta Corriente
  const [institutionNumber, setInstitutionNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [clabe, setClabe] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [transitNumber, setTransitNumber] = useState('');
  const [iban, setIban] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('');
  const [taxId, setTaxId] = useState('');
  const [errMessage, setErrMessage] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [toAccount, setToAccount] = useState({ name: '', bank: '', icon: '' });
  const [transaction, setTransaction] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [exchangeRate_, setExchangeRate_] = useState(0);
  const [dollarValue, setDollarValue] = useState('');
  const [copValue, setCopValue] = useState('');
  const [exchangeRate, setExchangeRate] = useState(4128.07);
  const [customBanks, setCustomBanks] = useState([]);
  // Saldo en PESOS del cliente. Se usa para decidir de donde sale el retiro:
  // si sus pesos alcanzan, no hay cambio de moneda y no se cobra comision.
  const [saldoCop, setSaldoCop] = useState(null);
  const [concept, setConcept] = useState('');

  const onChangeToAccount = async (account) => {
    if (submitting) return;
    setSubmitting(true);
    if (account.destination_currency) await getExchangeRate(account.destination_currency);

    setToAccount(account);
    setProgress(1);
    setSubmitting(false);
  };

  const handleRegisterBank = async (newBank) => {
    setCustomBanks([...customBanks, newBank]);
    setBank(newBank);
    await API.post('user/createBank', newBank);
  };

  const onChangeToAccount_ = async (account) => {
    if (submitting) return;
    setSubmitting(true);
    if (account.destination_currency) await getExchangeRate_();

    setToAccount(account);
    setProgress(1);
    setSubmitting(false);
  };

  // Colombia
  useEffect(() => {
    getExchangeRate_();
    // Si falla, saldoCop queda en null y todo sigue por el camino de siempre.
    API.get('user/getDashboardInfo')
      .then((r) => setSaldoCop(Number(r?.data?.data?.balance_cop || 0)))
      .catch(() => setSaldoCop(null));
  }, []);
  const getExchangeRate_ = async () => {
    const res = await API.get('transaction/get_exchange_rate');
    if (res.data.success == true) {
      setExchangeRate(res.data.rate);
    } else {
      console.log(res.data);
    }
  };

  const getExchangeRate = async (currency) => {
    if (currency == 'usd') setExchangeRate(1);
    else {
      await API.post('transaction/getExchangeRate', {
        from: 'usd',
        to: currency,
      })
        .then((res) => {
          console.log('res: ', res.data);
          // Apply 1% buffer to sell_rate to account for Bridge's execution spread
          // This ensures recipient always gets >= what was shown
          const conservativeRate = res.data.data.sell_rate * 0.99;
          setExchangeRate(conservativeRate);
        })
        .catch((err) => {
          console.log('err: ', err);
        });
    }
  };

  const setBankList = () => {
    let defaultBanks = [];
    if (country.isoCode3 == 'USA') defaultBanks = UsBanks;
    if (country.isoCode3 == 'COL') defaultBanks = ColombiaBanks;
    if (country.isoCode3 == 'EUR') defaultBanks = EuropeBanks;
    if (country.isoCode3 == 'MEX') defaultBanks = MexicoBanks;
    if (country.isoCode3 == 'BRA') defaultBanks = BrazilBanks;

    return [...defaultBanks, ...customBanks];
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchExternalAccounts = async () => {
    if (country.name == 'Colombia') {
      console.log('Colombia country: ');
      await API.get('user/getColombiaBeneficiaries')
        .then((res) => {
          console.log('res: ', res);
          setAccounts(res.data.data);
        })
        .catch((err) => {
          console.log('err: ', err);
          if (err.status == 401) navigate('/login');
        });
    } else if (
      country.name == 'Estados Unidos' ||
      country.name == 'México' ||
      country.name == 'Europa' ||
      country.name == 'Brasil'
    ) {
      await API.get('user/getExternalAccounts', {
        params: {
          currency: country.currency.toLowerCase(),
          paymentRail: paymentRoute.toLowerCase(),
        },
      })
        .then((res) => {
          console.log('res: ', res);
          setAccounts(res.data.data);
        })
        .catch((err) => {
          console.log('err: ', err);
          if (err.status == 401) navigate('/login');
        });
    } else {
      console.log('Asia countries: ');
    }
  };

  useEffect(() => {
    fetchExternalAccounts();
  }, [isNewAccount]);

  const onSaveNewAccount = async () => {
    if (submitting) return;
    setSubmitting(true);
    console.log({
      currency: country.currency.toLowerCase(),
      bankAccountType: getAccountType(country.currency.toLowerCase()),
      bankName: bank.name,
      bankImage: bank.image,
      bankAccountOwnerName: bankAccountOwnerName,
      firstName: firstName,
      lastName: lastName,
      businessName: businessName,
      recipientType: recipientType,
      address: {
        street: street,
        country: country.isoCode3 == 'EUR' ? countryIsoCode3 : country.isoCode3,
        state: state1,
        city: city,
        postalCode: postalCode,
      },
      paymentRail: paymentRoute.toLowerCase(),
    });

    if (country.name == 'Colombia') {
      if (
        !firstName ||
        !lastName ||
        !emailValidator(email) ||
        !docType ||
        !docNumber ||
        !street ||
        !state1 ||
        !city ||
        !postalCode ||
        !bank.name ||
        !bank.image ||
        !accountType ||
        !accountNumber
      )
        return;

      await API.post('user/createColombiaBeneficiary', {
        firstName: firstName,
        lastName: lastName,
        email: email,
        docType: docType,
        docNumber: docNumber,
        street: street,
        state: state1,
        city: city,
        postalCode: postalCode,
        bank: bank,
        accountType: accountType == 'savings' ? 'Ahorro' : 'Corriente',
        accountNumber: accountNumber,
      })
        .then((res) => {
          console.log('res: ', res.data);
          setErrMessage('');
          // setProgress(1);
          setIsNewAccount(false);
        })
        .catch((err) => {
          console.log('err: ', err);
          if (err.status == 401) navigate('/login');

          const msg = err?.response?.data?.message || err?.message;
          setErrMessage(findMessage(msg));
        });
    } else if (
      country.name == 'Estados Unidos' ||
      country.name == 'México' ||
      country.name == 'Europa' ||
      country.name == 'Brasil'
    ) {
      console.log('=====> validator started <======');
      if (
        !bank.name ||
        !bank.image ||
        !recipientType ||
        !accountType ||
        !bankAccountOwnerName ||
        !street ||
        !state1 ||
        !city ||
        !postalCode
      )
        return;

      if (recipientType == 'individual' && (!firstName || !lastName)) return;
      if (recipientType == 'business' && !businessName) return;

      if (country.isoCode3 == 'USA' && (!accountNumber || !routingNumber)) return;
      if (country.isoCode3 == 'EUR' && !iban) return;
      if (country.isoCode3 == 'MEX' && (!clabe || clabe.length != 18)) return;
      if (country.isoCode3 == 'BRA' && (!pixKey || !pixKeyType || !taxId)) return;

      console.log('=====> validator passed <======');
      if (country.isoCode3 == 'USA' || country.isoCode3 == 'MEX' || country.isoCode3 == 'EUR' || country.isoCode3 == 'BRA') {
        let payload = {
          currency: country.currency.toLowerCase(),
          bankAccountType: getAccountType(country.currency.toLowerCase()),
          bankName: bank.name,
          bankImage: bank.image,
          bankAccountOwnerName: bankAccountOwnerName,
          firstName: firstName,
          lastName: lastName,
          businessName: businessName,
          recipientType: recipientType,
          address: {
            street: street,
            country: country.isoCode3 == 'EUR' ? countryIsoCode3 : country.isoCode3,
            state: state1,
            city: city,
            postalCode: postalCode,
          },
          paymentRail: paymentRoute.toLowerCase(),
        };
        if (country.isoCode3 == 'USA')
          payload.account = {
            rountingNumber: routingNumber,
            accountNumber: accountNumber,
            checkingOrSavings: accountType,
          };
        else if (country.isoCode3 == 'EUR') {
          payload.iban = {
            accountNumber: iban,
            country: country.isoCode3 == 'EUR' ? countryIsoCode3 : country.isoCode3,
          };
        } else if (country.isoCode3 == 'MEX') {
          payload.clabe = {
            accountNumber: clabe,
          };
        } else if (country.isoCode3 == 'BRA') {
          payload.pix = {
            key: pixKey,
            keyType: pixKeyType,
            taxId: taxId,
          };
        }

        console.log('payload: ', payload);

        await API.post('user/createExternalAccount', payload)
          .then((res) => {
            console.log('res: ', res);
            setErrMessage('');
            // setProgress(1);
            setIsNewAccount(false);
          })
          .catch((err) => {
            console.log('err: ', err);
            if (err.status == 401) navigate('/login');

            const msg = err?.response?.data?.message || err?.message;
            setErrMessage(findMessage(msg));
          });
      }
    } else {
      console.log('asia countries:');
    }

    setSubmitting(false);
  };

  // dollarValue and copValue are now managed bidirectionally by CurrencyConversionForm

  const getConfirmData = () => {
    let confirmData = [
      {
        title: 'Destinatario',
        content:
          country.isoCode3 == 'COL'
            ? toAccount.first_name + ' ' + toAccount.last_name
            : country.isoCode3 == 'USA' || country.isoCode3 == 'EUR' || country.isoCode3 == 'MEX' || country.isoCode3 == 'BRA'
            ? toAccount.external_account.account_owner_name
            : 'Not allowed country yet',
      },
      {
        title: 'Cuenta destino',
        content:
          country.isoCode3 == 'COL'
            ? toAccount.bank_name
            : country.isoCode3 == 'USA' || country.isoCode3 == 'EUR' || country.isoCode3 == 'MEX' || country.isoCode3 == 'BRA'
            ? toAccount.external_account.bank_name +
              ' ' +
              (country.isoCode3 == 'USA' ? toAccount.external_account.account.account_number : '')
            : 'Not allowed country yet',
      },
    ];
    if (country.name == 'México')
      confirmData.push({
        title: 'CLABE',
        content: toAccount.external_account.clabe.account_number,
      });

    if (country.name == 'Canadá') {
      confirmData.push({ title: 'Número transit', content: '98763' });
      confirmData.push({ title: 'Número de institución', content: '004' });
    }
    if (country.name == 'Europa') {
      confirmData.push({
        title: 'IBAN',
        content: toAccount.external_account.iban.account_number,
      });
    }
    if (country.name == 'Brasil') {
      confirmData.push({
        title: 'Llave PIX',
        content: toAccount.external_account.pix?.key || '',
      });
    }
    if (country.name == 'India') {
      confirmData.push({ title: 'BIC/SWIFT', content: 'BBVAESMMXXX' });
    }
    if (country.isoCode3 == 'USA') {
      confirmData.push({
        title: paymentRoute,
        content: toAccount.external_account.account.account_number,
      });
    }

    confirmData.push({
      title: 'Comisión',
      content: paymentRoute == 'ACH' ? 'Sin comisión' : `${import.meta.env.VITE_DEVELOPER_FEE}%`,
    });
    if (country.name !== 'Estados Unidos')
      confirmData.push({
        title: 'Cotización',
        content: `1 USD = ${exchangeRate} ${country.currency}`,
      });
    else
      confirmData.push({
        title: `Número de routing ${paymentRoute}`,
        content: toAccount.external_account.account.routing_number,
      });

    return confirmData;
  };

  const formattedDate = (date) => {
    const formatted = date
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(',', ' -');

    return formatted; // '01/02/2025 - 15:05'
  };

  const getFinalData = () => {
    return [
      {
        title: 'Tipo de transacción',
        content: 'Transferencia internacional',
      },
      {
        title: 'Destinatario',
        content:
          country.isoCode3 == 'COL'
            ? toAccount.first_name + ' ' + toAccount.last_name
            : country.isoCode3 == 'USA' || country.isoCode3 == 'EUR' || country.isoCode3 == 'MEX' || country.isoCode3 == 'BRA'
            ? toAccount.external_account.account_owner_name
            : 'Not allowed country yet',
      },
      {
        title: 'Remitente', // sender
        content: snap.user.firstName + ' ' + snap.user.lastName,
      },
      {
        title: 'Fecha y hora',
        content: formattedDate(new Date(transaction?.createdAt)),
      },
      {
        title: 'Cuenta destino',
        content: transaction.bank_name + ' ' + transaction.account_number,
      },
      {
        title: 'Concepto',
        content: concept || 'Sin concepto',
      },
    ];
  };

  const onOffRamp = async () => {
    if (submitting) return;
    setSubmitting(true);

    // ¿SALE DE PESOS O DE DOLARES?
    // Los pesos que entraron por PSE nunca fueron dolares: sacarlos no es una
    // conversion y no lleva comision. Antes esta pantalla mandaba TODO por el
    // off-ramp de dolares, asi que un cliente de acciones colombianas pagaba 1%
    // (con piso de US$5) por sacar su propia plata — y el mismo cliente NO pagaba
    // si lo pedia por el Oraculo. El precio no puede depender de por donde entre.
    //
    // Guardas a proposito: solo toma el camino nuevo si el saldo en pesos alcanza
    // Y el monto pasa el minimo de operacion. Ante cualquier duda, el flujo de
    // siempre, intacto.
    const MIN_OP_COP = 50000;
    const montoCop = Number(copValue || 0);
    const saleDePesos = country.isoCode3 == 'COL'
      && saldoCop !== null && montoCop > 0
      && saldoCop >= montoCop && montoCop >= MIN_OP_COP;

    if (saleDePesos) {
      const urlV2 = import.meta.env.VITE_OPERATIONS_API_URL || 'https://api-beta-v2.flowexpay.com/api';
      // requestId idempotente: si el timeout hace reintentar, el backend NO dispersa dos veces.
      if (!retiroRequestIdRef.current) retiroRequestIdRef.current = crypto.randomUUID();
      await axios.post(`${urlV2}/transaction/retiroCop`, {
        colombiaBeneficiaryId: toAccount._id,
        amountCop: montoCop,
        concept: concept,
        requestId: retiroRequestIdRef.current,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 })
        .then((res) => {
          retiroRequestIdRef.current = null; // exito: el proximo retiro es otro intento
          setTransaction(res.data);
          setProgress(3);
          setSubmitting(false);
        })
        .catch((err) => {
          const msg = err?.response?.data?.message || err?.message;
          const st = err?.response?.status;
          // 409 = ese retiro YA esta en proceso (tx CREATED con este requestId).
          // CONSERVAR el id — limpiarlo aqui generaba un UUID nuevo al reintentar
          // y abria la puerta al doble debito + doble dispersion (hallazgo ALTO
          // de la re-auditoria, 8-sep). Se bloquea el boton 15s para no martillar.
          if (st === 409) {
            setNotification({ message: 'Ese retiro ya está en proceso. Espera un momento…', type: 'error' });
            setTimeout(() => setSubmitting(false), 15000);
            return;
          }
          // 4xx restantes (monto, saldo, beneficiario) = rechazo definitivo: el
          // proximo intento es una operacion NUEVA. Error de red/5xx conserva el
          // id para reintentar sin riesgo de doble dispersion.
          if (st && st >= 400 && st < 500 && st !== 401) retiroRequestIdRef.current = null;
          if (st == 401) navigate('/login');
          setNotification({ message: findMessage(msg), type: 'error' });
          setSubmitting(false);
        });
      return;
    }

    if (country.isoCode3 == 'COL') {
      await API.post('transaction/offRampByPayvalida', {
        country: country.isoCode3,
        colombiaBeneficiaryId: toAccount._id,
        amount: copValue,
        exchangeRate: exchangeRate,
        concept: concept,
        asset: {
          assetId: 'SOL_USDC_PTHX',
          symbol: 'USDC',
        },
      })
        .then((res) => {
          console.log('off ramp offRampByPayvalida res: ', res);
          setTransaction(res.data);
          setProgress(3);
          setSubmitting(false);
        })
        .catch((err) => {
          console.log('off ramp err: ', err);
          const msg = err?.response?.data?.message || err?.message;
          if (err.status == 401) navigate('/login');

          setNotification({
            message: findMessage(msg),
            type: 'error',
          });
          setSubmitting(false);
        });
    } else if (
      country.isoCode3 == 'USA' ||
      country.isoCode3 == 'EUR' ||
      country.isoCode3 == 'MEX' ||
      country.isoCode3 == 'BRA'
    ) {
      console.log('Off ramp request sent');
      await API.post('transaction/offRampByBridge', {
        liquidationAddressId: toAccount._id,
        amount: copValue,
        currency: country.currency,
        country: country.isoCode3,
        exchangeRate: exchangeRate,
        concept: concept,
      })
        .then((res) => {
          console.log('off Ramp: ', res.data);
          setTransaction(res.data);
          setProgress(3);
          setSubmitting(false);
        })
        .catch((err) => {
          console.log('off ramp err: ', err);
          const msg = err?.response?.data?.message || err?.message;
          if (err.status == 401) navigate('/login');

          setNotification({
            message: findMessage(msg),
            type: 'error',
          });
          setSubmitting(false);
        });
    } else {
      console.log('Not supported country yet');
      setSubmitting(false);
    }
  };

  const processImageURI = (uri) => {
    if (!uri) return null;

    let baseURL = import.meta.env.VITE_IMAGE_URL;
    const replacedUri = uri.replace('/src', baseURL);

    if (replacedUri.startsWith('http://') || replacedUri.startsWith('https://')) {
      return { uri: replacedUri };
    }

    const fullUri = `${baseURL}${replacedUri}`;
    console.log({ uri: fullUri });
    return { uri: fullUri };
  };

  return (
    <div className="relative py-8 flex items-center justify-center">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="w-153.5 fx-glass border border-[var(--fx-border)] rounded-[20px] p-6 space-y-6">
        {progress == 0 && (
          <>
            <TransferHeader
              headerTitle={isNewAccount ? 'Registrar cuenta nueva' : 'Usar cuenta registrada'}
              description={
                isNewAccount
                  ? 'Ingresa los datos de la cuenta del destinatario'
                  : country?.isoCode3 == 'COL'
                    ? 'Si tu saldo en pesos cubre el monto, sale de COP sin conversión. Desde USD aplica conversión.'
                    : 'Selecciona la cuenta a la que deseas transferir'
              }
              goBack={onToPrev}
            />

            <div className="flex gap-2 p-1 rounded-xl border border-[var(--fx-border)] bg-[rgba(255,255,255,0.03)]" role="tablist">
              <button
                type="button"
                className={`flex-1 py-2.5 px-3 rounded-lg text-xs sm:text-sm font-semibold transition ${!isNewAccount ? 'bg-[var(--fx-accent)] text-white shadow-[0_0_20px_var(--fx-accent-glow)]' : 'text-[var(--fx-muted)]'}`}
                onClick={() => { setIsNewAccount(false); setErrMessage(''); }}
              >
                Usar cuenta registrada
              </button>
              <button
                type="button"
                className={`flex-1 py-2.5 px-3 rounded-lg text-xs sm:text-sm font-semibold transition ${isNewAccount ? 'bg-[var(--fx-accent)] text-white shadow-[0_0_20px_var(--fx-accent-glow)]' : 'text-[var(--fx-muted)]'}`}
                onClick={() => { setIsNewAccount(true); setErrMessage(''); }}
              >
                Registrar cuenta nueva
              </button>
            </div>

            {isNewAccount ? (
              <div className="space-y-6">
                {country.name != 'Colombia' && (
                  <NormalSelectbox
                    title="Tipo de destinatario"
                    options={Roles}
                    selected={recipientType}
                    onChange={setRecipientType}
                  />
                )}

                {/* user name or business name */}
                {(recipientType == '' || recipientType == 'individual') && (
                  <div className="grid grid-cols-2 gap-4">
                    <Inputbox
                      title="Nombre de pila"
                      placeholder="Juan"
                      value={firstName}
                      onChange={setFirstName}
                    />
                    <Inputbox
                      title="Apellido"
                      placeholder="Perez"
                      value={lastName}
                      onChange={setLastName}
                    />
                  </div>
                )}
                {recipientType == 'business' && (
                  <Inputbox
                    title="Nombre de la empresa"
                    placeholder="Soluciones Andinas S.A.S."
                    value={businessName}
                    onChange={setBusinessName}
                  />
                )}

                {country.name == 'Colombia' && (
                  <>
                    <Inputbox
                      title="Correo electrónico"
                      placeholder="ejemplo@mail.com"
                      value={email}
                      onChange={setEmail}
                    />
                    <NormalSelectbox
                      title="Tipo de documento"
                      options={IDType}
                      selected={docType}
                      onChange={setDocType}
                    />
                    <Inputbox
                      title="Documento del destinatario"
                      placeholder="1123456789"
                      value={docNumber}
                      onChange={setDocNumber}
                    />
                  </>
                )}

                {/* address information */}
                {country.isoCode3 == 'EUR' ? (
                  <NormalSelectbox
                    title="País de destino"
                    options={EuropeRegion}
                    selected={countryIsoCode3}
                    onChange={setCountryIsoCode3}
                  />
                ) : (
                  <Inputbox
                    disabled={true}
                    title="País"
                    placeholder="USA"
                    value={country.name}
                  />
                )}
                <Inputbox
                  title="Dirección"
                  placeholder="923 Folsom Street"
                  value={street}
                  onChange={setStreet}
                />
                <Inputbox title="Estado / Provincia" placeholder="CA" value={state1} onChange={setState1} />
                <Inputbox
                  title="Ciudad"
                  placeholder="San Francisco"
                  value={city}
                  onChange={setCity}
                />
                <Inputbox
                  title="Código postal"
                  placeholder="941070000"
                  value={postalCode}
                  onChange={setPostalCode}
                />

                <Inputbox disabled={true} title="Moneda" placeholder="" value={country.currency} />
                <BankSelectbox
                  title="Banco"
                  options={setBankList()}
                  selected={bank}
                  onChange={setBank}
                  onRegisterBank={handleRegisterBank}
                  isoCode3={country.isoCode3}
                />
                <Inputbox
                  title="Nombre del titular de la cuenta bancaria"
                  placeholder="Juan Perez"
                  value={bankAccountOwnerName}
                  onChange={setBankAccountOwnerName}
                />

                <NormalSelectbox
                  title="Tipo de cuenta"
                  options={WalletType}
                  selected={accountType}
                  onChange={setAccountType}
                />

                {country.name == 'Canadá' && (
                  <Inputbox
                    title="Número de Institución"
                    placeholder="000"
                    value={institutionNumber}
                    onChange={setInstitutionNumber}
                  />
                )}

                {country.isoCode3 != 'EUR' && country.isoCode3 != 'MEX' && country.isoCode3 != 'BRA' && (
                  <Inputbox
                    title="Número de cuenta"
                    placeholder="000123456789"
                    value={accountNumber}
                    onChange={setAccountNumber}
                  />
                )}

                {country.name == 'México' && (
                  <Inputbox
                    title="CLABE"
                    placeholder="00000000000000"
                    value={clabe}
                    onChange={setClabe}
                  />
                )}

                {country.name == 'Estados Unidos' && (
                  <Inputbox
                    title={`Número de routing ${paymentRoute}`}
                    placeholder="000000000"
                    value={routingNumber}
                    onChange={setRoutingNumber}
                  />
                )}

                {country.name == 'Canadá' && (
                  <Inputbox
                    title="Número Transit"
                    placeholder="98765"
                    value={transitNumber}
                    onChange={setTransitNumber}
                  />
                )}

                {country.name == 'Europa' && (
                  <Inputbox
                    title="IBAN"
                    placeholder="ES9121000418450200051332"
                    value={iban}
                    onChange={setIban}
                  />
                )}

                {country.name == 'Brasil' && (
                  <>
                    <NormalSelectbox
                      title="Tipo de llave PIX"
                      options={PixKeyTypes}
                      selected={pixKeyType}
                      onChange={setPixKeyType}
                    />
                    <Inputbox
                      title="Llave PIX"
                      placeholder={pixKeyType == 'cpf' ? '000.000.000-00' : pixKeyType == 'email' ? 'ejemplo@mail.com' : pixKeyType == 'phone' ? '+5511999999999' : 'Clave PIX'}
                      value={pixKey}
                      onChange={setPixKey}
                    />
                    <Inputbox
                      title="CPF / CNPJ del destinatario"
                      placeholder="000.000.000-00"
                      value={taxId}
                      onChange={setTaxId}
                    />
                  </>
                )}

                {country.name == 'India' && (
                  <Inputbox
                    title="Código IFSC"
                    placeholder="98765"
                    value={bankCode}
                    onChange={setBankCode}
                  />
                )}

                {country.isoCode3 != 'USA' &&
                  country.isoCode3 != 'EUR' &&
                  country.isoCode3 != 'MEX' &&
                  paymentRoute && (
                    <>
                      {country.name == 'China' ? (
                        <Inputbox
                          title={paymentRoute == 'BIC/SWIFT' ? 'BIC/SWIFT' : 'Código CNAPS'}
                          placeholder={paymentRoute == 'BIC/SWIFT' ? 'AAAA0000XXX' : '000'}
                          value={bankCode}
                          onChange={setBankCode}
                        />
                      ) : (
                        <Inputbox
                          title={paymentRoute}
                          placeholder={paymentRoute == 'BIC/SWIFT' ? 'AAAA0000XXX' : '000'}
                          value={bankCode}
                          onChange={setBankCode}
                        />
                      )}
                    </>
                  )}

                <div>
                  <p className="font-[poppins] text-sm text-[#F04438]">{errMessage}</p>
                </div>

                <span className="float-right">
                  <div className="space-x-2.5">
                    <NormalButton
                      customClass="text-[#092F7B] bg-white"
                      text="Cancelar"
                      onClick={() => {
                        setIsNewAccount(false);
                        setErrMessage('');
                      }}
                    />
                    <NormalButton
                      customClass="text-[#FDFDFD] bg-[#092F7B]"
                      text="Agregar cuenta"
                      onClick={onSaveNewAccount}
                    />
                  </div>
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <Searchbar
                    customClass="text-sm leading-[20px] py-[7px]"
                    placeholder="Buscar destinatario..."
                    searchKeyword={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                  />
                  <IconButton
                    bgColor="bg-[#FDFDFD]"
                    icon={PlusIcon}
                    text="Nueva cuenta"
                    textColor="min-w-25 text-[#092F7B] leading-[22px]"
                    onClick={() => setIsNewAccount(true)}
                  />
                </div>

                <div className="space-y-2 pb-6">
                  {(country.name == 'Estados Unidos' ||
                    country.name == 'México' ||
                    country.name == 'Europa' ||
                    country.name == 'Brasil') &&
                    accounts
                      .filter((account) =>
                        account.external_account.account_owner_name.includes(searchKeyword)
                      )
                      .map((account, idx) => (
                        <AccountItem
                          key={idx}
                          account={{
                            bank_image: processImageURI(account.external_account.bank_image).uri,
                            account_owner_name: account.external_account.account_owner_name,
                            bank_name: account.external_account.bank_name,
                            account_number:
                              country.isoCode3 == 'USA'
                                ? account.external_account.account.account_number
                                : country.isoCode3 == 'EUR'
                                ? account.external_account.iban.account_number
                                : country.isoCode3 == 'BRA'
                                ? account.external_account.pix?.key || ''
                                : account.external_account.clabe.account_number,
                          }}
                          endIcon={<ArrowRightIcon />}
                          onClick={() => onChangeToAccount(account)}
                        />
                      ))}

                  {country.name == 'Colombia' &&
                    accounts
                      .filter((account) =>
                        (account.first_name + ' ' + account.last_name).includes(searchKeyword)
                      )
                      .map((account, idx) => (
                        <AccountItem
                          key={idx}
                          account={{
                            bank_image: processImageURI(account.bank_image).uri,
                            account_owner_name: account.first_name + ' ' + account.last_name,
                            bank_name: account.bank_name,
                            account_number: account.bank_account_number,
                          }}
                          endIcon={<ArrowRightIcon />}
                          onClick={() => onChangeToAccount_(account)}
                        />
                      ))}
                </div>
              </>
            )}
          </>
        )}

        {progress == 1 && (
          <>
            <CurrencyConversionForm
              country={country.name}
              title={isNewAccount ? 'Transferir a cuenta en Colombia' : 'Transferir'}
              toAccount={toAccount}
              dollarValue={dollarValue}
              setDollarValue={setDollarValue}
              flag={country.icon}
              localCurrency={country.currency}
              localCurrencyValue={copValue}
              setLocalCurrencyValue={setCopValue}
              exchangeRate={exchangeRate}
              setProgress={setProgress}
              exchangeRate_={exchangeRate_}
              paymentRoute={paymentRoute}
            />
            <div style={{ marginTop: '16px', padding: '0 16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#414651', display: 'block', marginBottom: '6px' }}>
                Concepto / Referencia (opcional)
              </label>
              <textarea
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder="Ej: Pago factura #123 - Cliente María López"
                maxLength={255}
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #D5D7DA',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  resize: 'none',
                  outline: 'none',
                }}
              />
              <span style={{ fontSize: '12px', color: '#717680', marginTop: '4px', display: 'block' }}>
                {concept.length}/255 — El destinatario verá este concepto
              </span>
            </div>
          </>
        )}

        {progress == 2 && (
          <TransferConfirmForm
            onCancel={goBack}
            onOffRamp={() => setShowSmsModal(true)}
            setProgress={setProgress}
            amount={copValue}
            currency={country.currency}
            dollarAmount={dollarValue}
            feePercent={
              // Si sale de los pesos del cliente no hay conversion, asi que la
              // comision es 0. Mostrar la de siempre seria cobrarle en pantalla
              // algo que el backend no le va a cobrar.
              (country.isoCode3 == 'COL' && saldoCop !== null
                && Number(copValue || 0) > 0 && saldoCop >= Number(copValue || 0)
                && Number(copValue || 0) >= 50000)
                ? 0
                : (paymentRoute == 'ACH' ? 0 : parseFloat(import.meta.env.VITE_DEVELOPER_FEE) || 0.5)
            }
            exchangeRate={exchangeRate}
            data={getConfirmData()}
            isNewAccount={isNewAccount}
            submitting={submitting}
            concept={concept}
            countryName={country.name}
          />
        )}

        {progress >= 3 && (
          <TransferFinalForm
            onCancel={goBack}
            setProgress={setProgress}
            amount={
              parseFloat(copValue) + (paymentRoute == 'ACH' ? 0 : parseFloat(copValue) * 0.01)
            }
            data={getFinalData()}
          />
        )}
      </div>

      {/* Verificacion SMS antes de enviar dinero (cross-border) */}
      <SmsVerifyModal
        visible={showSmsModal}
        title="Confirmar envio"
        description="Estas autorizando un envio de dinero. Ingresa el codigo SMS para confirmar la operacion."
        onVerified={() => { setShowSmsModal(false); onOffRamp(); }}
        onClose={() => setShowSmsModal(false)}
      />
    </div>
  );
}
